import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Chip, IconButton, MenuItem,
  InputAdornment, CircularProgress, Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon, Star as StarIcon, StarBorder as StarBorderIcon,
  Delete as DeleteIcon, Add as AddIcon, Biotech as BiotechIcon,
  LocalHospital as HospitalIcon, Hotel as HotelIcon, Description as CertIcon,
  EventNote as JustIcon, Edit as LibreIcon, Close as CloseIcon,
  PictureAsPdf as PdfIcon, Print as PrintIcon,
  ModeEdit as EditIcon,
} from '@mui/icons-material';
import { exportOrdenHospitalizacionPDF } from './exportOrdenHospPDF';
import { exportCertificadoPDF } from './exportCertificadoPDF';
import { exportJustificantePDF } from './exportJustificantePDF';
import { exportInterconsultaPDF } from './exportInterconsultaPDF';
import { exportOrdenLaboratorioPDF } from './exportOrdenLaboratorioPDF';

const TIPOS_ORDEN = [
  { value: 'laboratorio', label: 'Orden de laboratorio', icon: <BiotechIcon />, color: '#f59e0b' },
  { value: 'interconsulta', label: 'Orden de interconsulta', icon: <HospitalIcon />, color: '#0ea5e9' },
  { value: 'hospitalizacion', label: 'Orden de hospitalización', icon: <HotelIcon />, color: '#ef4444' },
  { value: 'certificado', label: 'Certificado médico', icon: <CertIcon />, color: '#10b981' },
  { value: 'justificante', label: 'Justificante médico', icon: <JustIcon />, color: '#8b5cf6' },
  { value: 'libre', label: 'Orden libre', icon: <LibreIcon />, color: '#64748b' },
];

export default function OrdenesTab({ consultaId, userId, ordenes = [], onOrdenesChange, API_URL, paciente }) {
  const [tipoSeleccionado, setTipoSeleccionado] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [favoritos, setFavoritos] = useState([]);
  const [estudiosSeleccionados, setEstudiosSeleccionados] = useState([]);
  const [indicaciones, setIndicaciones] = useState('');
  const [ordenEditandoId, setOrdenEditandoId] = useState(null);
  const [textoLibre, setTextoLibre] = useState('');
  const [especialidad, setEspecialidad] = useState('');
  const [motivo, setMotivo] = useState('');
  const [nombreEspecialista, setNombreEspecialista] = useState('');
  const [telefonoEspecialista, setTelefonoEspecialista] = useState('');
  const [indicacionesHosp, setIndicacionesHosp] = useState('');
  const [indicacionesPaciente, setIndicacionesPaciente] = useState('');
  const [diagnosticoCert, setDiagnosticoCert] = useState('');
  const [observacionesCert, setObservacionesCert] = useState('');
  const [diagnosticoJust, setDiagnosticoJust] = useState('');
  const [observacionesJust, setObservacionesJust] = useState('');

  const pais = 'MX';

  const cargarFavoritos = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_URL}/emr/estudios/favoritos?pais=${pais}`, { headers: { 'X-User-Id': userId } });
      if (res.ok) {
        const data = await res.json();
        setFavoritos(Array.isArray(data) ? data : (data.favoritos || []));
      }
    } catch (err) { console.error(err); }
  }, [userId]);

  useEffect(() => { cargarFavoritos(); }, [cargarFavoritos]);

  useEffect(() => {
    if (tipoSeleccionado === 'laboratorio') cargarFavoritos();
  }, [tipoSeleccionado, cargarFavoritos]);

  useEffect(() => {
    if (!busqueda || busqueda.length < 1) { setResultados([]); return; }
    const timer = setTimeout(async () => {
      setBuscando(true);
      try {
        const res = await fetch(`${API_URL}/emr/estudios/buscar?q=${encodeURIComponent(busqueda)}&pais=${pais}&limit=20`, {
          headers: { 'X-User-Id': userId }
        });
        if (res.ok) {
          const data = await res.json();
          setResultados(data.estudios || data.resultados || []);
        }
      } catch (err) { console.error('Error búsqueda estudios:', err); }
      setBuscando(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [busqueda, userId]);

  const toggleFavorito = async (estudio) => {
    const esFav = favoritos.some(f => f.id === estudio.id);
    if (esFav) {
      setFavoritos(favoritos.filter(f => f.id !== estudio.id));
      fetch(`${API_URL}/emr/estudios/favoritos/${estudio.id}`, { method: 'DELETE', headers: { 'X-User-Id': userId } }).catch(console.error);
    } else {
      setFavoritos([...favoritos, estudio]);
      fetch(`${API_URL}/emr/estudios/favoritos/${estudio.id}`, { method: 'POST', headers: { 'X-User-Id': userId } }).catch(console.error);
    }
  };

  const agregarEstudio = (estudio) => {
    if (estudiosSeleccionados.find(e => e.id === estudio.id)) return;
    setEstudiosSeleccionados([...estudiosSeleccionados, estudio]);
  };

  const quitarEstudio = (id) => {
    setEstudiosSeleccionados(estudiosSeleccionados.filter(e => e.id !== id));
  };

  const guardarOrden = async () => {
    if (!consultaId) return;
    let contenido = {};
    if (tipoSeleccionado === 'laboratorio') {
      contenido = { estudios: estudiosSeleccionados.map(e => ({ id: e.id, codigo: e.codigo, nombre: e.nombre })) };
    } else if (tipoSeleccionado === 'interconsulta') {
      contenido = {
        especialidad,
        motivo,
        ...(nombreEspecialista && { nombre_especialista: nombreEspecialista }),
        ...(telefonoEspecialista && { telefono_especialista: telefonoEspecialista }),
      };
    } else if (tipoSeleccionado === 'certificado') {
      contenido = {
        paciente: paciente?.nombre_completo || '',
        sexo: paciente?.sexo || '',
        diagnostico: diagnosticoCert,
        observaciones: observacionesCert,
      };
    } else if (tipoSeleccionado === 'justificante') {
      contenido = {
        paciente: paciente?.nombre_completo || '',
        sexo: paciente?.sexo || '',
        diagnostico: diagnosticoJust,
        observaciones: observacionesJust,
      };
    } else if (tipoSeleccionado === 'hospitalizacion') {
      contenido = {
        paciente: paciente?.nombre_completo || '',
        indicaciones_hospitalarias: indicacionesHosp,
        indicaciones_paciente: indicacionesPaciente,
      };
    } else {
      contenido = { texto: textoLibre };
    }
    try {
      const res = await fetch(`${API_URL}/emr/consultas/${consultaId}/ordenes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify({ tipo: tipoSeleccionado, contenido, indicaciones: indicaciones || null }),
      });
      if (res.ok) {
        const nueva = await res.json();
        // Si estamos editando, eliminar la vieja después de crear la nueva
        let listaActualizada = [...ordenes, nueva];
        if (ordenEditandoId) {
          try {
            await fetch(`${API_URL}/emr/ordenes/${ordenEditandoId}`, { method: 'DELETE', headers: { 'X-User-Id': userId } });
            listaActualizada = listaActualizada.filter(o => o.id !== ordenEditandoId);
          } catch (e) { console.error('No se pudo eliminar la orden anterior:', e); }
          setOrdenEditandoId(null);
        }
        onOrdenesChange(listaActualizada);
        setTipoSeleccionado('');
        setEstudiosSeleccionados([]);
        setIndicaciones('');
        setTextoLibre('');
        setEspecialidad('');
        setMotivo('');
        setNombreEspecialista('');
        setTelefonoEspecialista('');
        setIndicacionesHosp('');
        setIndicacionesPaciente('');
        setDiagnosticoCert('');
        setObservacionesCert('');
        setDiagnosticoJust('');
        setObservacionesJust('');
        setBusqueda('');
      }
    } catch (err) { console.error(err); }
  };

  const eliminarOrden = async (id) => {
    try {
      await fetch(`${API_URL}/emr/ordenes/${id}`, { method: 'DELETE', headers: { 'X-User-Id': userId } });
      onOrdenesChange(ordenes.filter(o => o.id !== id));
    } catch (err) { console.error(err); }
  };

  // Editar orden: carga datos en formulario y MARCA la antigua para reemplazo (NO la borra hasta que se guarde)
  const editarOrden = (o) => {
    setTipoSeleccionado(o.tipo);
    setIndicaciones(o.indicaciones || '');
    if (o.tipo === 'laboratorio') {
      setEstudiosSeleccionados(o.contenido?.estudios || []);
    } else if (o.tipo === 'interconsulta') {
      setEspecialidad(o.contenido?.especialidad || '');
      setMotivo(o.contenido?.motivo || '');
      setNombreEspecialista(o.contenido?.nombre_especialista || '');
      setTelefonoEspecialista(o.contenido?.telefono_especialista || '');
    } else if (o.tipo === 'hospitalizacion') {
      setIndicacionesHosp(o.contenido?.indicaciones_hospitalarias || '');
      setIndicacionesPaciente(o.contenido?.indicaciones_paciente || '');
    } else if (o.tipo === 'certificado') {
      setDiagnosticoCert(o.contenido?.diagnostico || '');
      setObservacionesCert(o.contenido?.observaciones || '');
    } else if (o.tipo === 'justificante') {
      setDiagnosticoJust(o.contenido?.diagnostico || '');
      setObservacionesJust(o.contenido?.observaciones || '');
    } else if (o.tipo === 'libre') {
      setTextoLibre(o.contenido?.texto || '');
    }
    // IMPORTANTE: NO eliminamos. Solo marcamos como "editando" — la vieja se borra al guardar
    setOrdenEditandoId(o.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelarEdicion = () => {
    setOrdenEditandoId(null);
    setTipoSeleccionado('');
    setIndicaciones('');
    setEstudiosSeleccionados([]);
    setEspecialidad(''); setMotivo('');
    setNombreEspecialista(''); setTelefonoEspecialista('');
    setIndicacionesHosp(''); setIndicacionesPaciente('');
    setDiagnosticoCert(''); setObservacionesCert('');
    setDiagnosticoJust(''); setObservacionesJust('');
    setTextoLibre('');
  };

  const tipoInfo = TIPOS_ORDEN.find(t => t.value === tipoSeleccionado);

  const ordenesDelTipo = (tipo) => ordenes.filter(o => o.tipo === tipo).length;

  return (
    <Box>
      {/* Banner de modo edición */}
      {ordenEditandoId && (
        <Box sx={{ mb: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#fffbeb', border: '1.5px solid #fbbf24', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon sx={{ fontSize: 18, color: '#92400e' }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>
              Editando documento — los cambios se aplicarán al guardar
            </Typography>
          </Box>
          <Button size="small" onClick={cancelarEdicion}
            sx={{ textTransform: 'none', fontSize: 11, fontWeight: 600, color: '#92400e', border: '1px solid #fbbf24', borderRadius: 2 }}>
            Cancelar edición
          </Button>
        </Box>
      )}
      {/* ── Layout master-detail ── */}
      <Box sx={{ display: 'flex', gap: 0, minHeight: 420, borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e8f0', background: '#fff' }}>

        {/* ── Sidebar izquierda ── */}
        <Box sx={{
          width: { xs: 56, sm: 190 }, flexShrink: 0,
          background: '#f8fafc', borderRight: '1px solid #e2e8f0',
          display: 'flex', flexDirection: 'column',
        }}>
          {TIPOS_ORDEN.map(t => {
            const activo = tipoSeleccionado === t.value;
            const count = ordenesDelTipo(t.value);
            return (
              <Box
                key={t.value}
                onClick={() => setTipoSeleccionado(t.value)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.2,
                  px: { xs: 1, sm: 2 }, py: 1.8,
                  cursor: 'pointer', position: 'relative',
                  transition: 'all 0.2s',
                  background: activo ? '#fff' : 'transparent',
                  borderBottom: '1px solid #f1f5f9',
                  '&:hover': { background: activo ? '#fff' : '#f1f5f9' },
                  '&::before': activo ? {
                    content: '""', position: 'absolute', left: 0, top: 0, bottom: 0,
                    width: 3, borderRadius: '0 3px 3px 0',
                    background: t.color,
                  } : {},
                }}
              >
                <Box sx={{
                  width: 32, height: 32, borderRadius: 2, flexShrink: 0,
                  background: activo ? t.color : `${t.color}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s',
                  boxShadow: activo ? `0 4px 12px ${t.color}40` : 'none',
                }}>
                  {React.cloneElement(t.icon, { sx: { fontSize: 18, color: activo ? '#fff' : t.color } })}
                </Box>
                <Box sx={{ display: { xs: 'none', sm: 'block' }, flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: 12, fontWeight: activo ? 700 : 500, lineHeight: 1.2,
                    color: activo ? '#1e293b' : '#64748b',
                  }} noWrap>
                    {t.label.replace('Orden de ', '').replace('Orden ', '')}
                  </Typography>
                  {count > 0 && (
                    <Typography sx={{ fontSize: 10, color: t.color, fontWeight: 600 }}>
                      {count} generada{count > 1 ? 's' : ''}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* ── Panel derecho: contenido ── */}
        <Box sx={{ flex: 1, p: { xs: 1.5, sm: 2.5 }, overflowY: 'auto', maxHeight: 500 }}>

          {/* Estado vacío */}
          {!tipoSeleccionado && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 300, color: '#94a3b8' }}>
              <BiotechIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1.5 }} />
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#94a3b8' }}>
                Seleccione un tipo de documento
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#cbd5e1', mt: 0.5 }}>
                Elija una opción del menú izquierdo para comenzar
              </Typography>
            </Box>
          )}

          {/* Formulario según tipo */}
          {tipoSeleccionado === 'laboratorio' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {/* ── Columna izquierda: selección ── */}
          <Box sx={{ p: 2, borderRadius: 3, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
            {/* Favoritos */}
            {favoritos.length > 0 && (
              <Box sx={{
                mb: 2, p: 1.5, borderRadius: 2.5,
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a40 100%)',
                border: '1px solid #fcd34d',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <StarIcon sx={{ fontSize: 15, color: '#f59e0b' }} />
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#92400e', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                    Mis favoritos
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {favoritos.map(f => {
                    const yaSeleccionado = estudiosSeleccionados.find(e => e.id === f.id);
                    return (
                      <Chip
                        key={f.id}
                        label={f.nombre}
                        size="small"
                        onClick={() => !yaSeleccionado && agregarEstudio(f)}
                        sx={{
                          cursor: yaSeleccionado ? 'default' : 'pointer',
                          fontWeight: 600, fontSize: 11, height: 26,
                          bgcolor: yaSeleccionado ? '#fde68a' : '#fff',
                          color: yaSeleccionado ? '#92400e' : '#475569',
                          border: `1px solid ${yaSeleccionado ? '#f59e0b' : '#fde68a'}`,
                          opacity: yaSeleccionado ? 0.6 : 1,
                          '&:hover': { bgcolor: yaSeleccionado ? '#fde68a' : '#fefce8' },
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Buscador */}
            <TextField
              fullWidth size="small"
              placeholder="Buscar examen (ej: biometría, glucosa, Rx tórax...)"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} /></InputAdornment>,
                endAdornment: buscando ? <CircularProgress size={16} /> : null,
              }}
              sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: 2, background: '#fff', fontSize: 13 } }}
            />

            {/* Resultados */}
            <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
              {resultados.map(r => {
                const seleccionado = estudiosSeleccionados.find(e => e.id === r.id);
                const esFav = favoritos.some(f => f.id === r.id);
                return (
                  <Box key={r.id} sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    p: 0.8, mb: 0.4, borderRadius: 1.5,
                    cursor: seleccionado ? 'default' : 'pointer',
                    background: seleccionado ? '#fef3c7' : '#fff',
                    border: `1px solid ${seleccionado ? '#fcd34d' : '#f1f5f9'}`,
                    opacity: seleccionado ? 0.5 : 1,
                    transition: 'all 0.15s',
                    '&:hover': { background: seleccionado ? '#fef3c7' : '#fefce8', borderColor: '#fde68a' },
                  }}
                    onClick={() => !seleccionado && agregarEstudio(r)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flex: 1, minWidth: 0 }}>
                      <Chip label={r.codigo} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 700, bgcolor: '#e0e7ff', color: '#4338ca', '& .MuiChip-label': { px: 0.6 } }} />
                      <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#1e293b' }} noWrap>{r.nombre}</Typography>
                      {r.subcategoria && <Typography sx={{ fontSize: 9, color: '#94a3b8', flexShrink: 0 }}>· {r.subcategoria}</Typography>}
                    </Box>
                    <Tooltip title={esFav ? 'Quitar de favoritos' : 'Agregar a favoritos'}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleFavorito(r); }} sx={{ p: 0.3, flexShrink: 0 }}>
                        {esFav
                          ? <StarIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                          : <StarBorderIcon sx={{ fontSize: 16, color: '#cbd5e1', '&:hover': { color: '#f59e0b' } }} />
                        }
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })}
              {!buscando && busqueda.length >= 1 && resultados.length === 0 && (
                <Typography sx={{ textAlign: 'center', color: '#94a3b8', fontSize: 12, py: 3 }}>
                  No se encontraron exámenes para "{busqueda}"
                </Typography>
              )}
              {!busqueda && resultados.length === 0 && favoritos.length === 0 && (
                <Typography sx={{ textAlign: 'center', color: '#94a3b8', fontSize: 12, py: 3 }}>
                  Busca un examen para agregarlo a la orden
                </Typography>
              )}
            </Box>
          </Box>

          {/* ── Columna derecha: orden en construcción ── */}
          <Box sx={{
            p: 2, borderRadius: 3,
            background: estudiosSeleccionados.length > 0 ? '#fffbeb' : '#f8fafc',
            border: `2px solid ${estudiosSeleccionados.length > 0 ? '#fde68a' : '#e2e8f0'}`,
            display: 'flex', flexDirection: 'column',
            transition: 'all 0.3s',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
              <Box sx={{
                width: 30, height: 30, borderRadius: 2,
                background: estudiosSeleccionados.length > 0 ? '#f59e0b' : '#94a3b8',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.3s',
              }}>
                <BiotechIcon sx={{ fontSize: 18, color: '#fff' }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#92400e', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Orden de laboratorio
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>
                  {estudiosSeleccionados.length} examen{estudiosSeleccionados.length !== 1 ? 'es' : ''} seleccionado{estudiosSeleccionados.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </Box>

            {/* Lista de seleccionados */}
            <Box sx={{ flex: 1, mb: 1.5 }}>
              {estudiosSeleccionados.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <BiotechIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                  <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                    Selecciona exámenes de la izquierda
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#cbd5e1' }}>
                    o usa tus favoritos para agregar rápido
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {estudiosSeleccionados.map((e, idx) => {
                    const esFav = favoritos.some(f => f.id === e.id);
                    return (
                      <Box key={e.id} sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        p: 1, borderRadius: 2, background: '#fff',
                        border: '1px solid #fde68a',
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#f59e0b', width: 18, textAlign: 'center' }}>{idx + 1}</Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#1e293b' }} noWrap>{e.nombre}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2, flexShrink: 0 }}>
                          <Tooltip title={esFav ? 'Quitar de favoritos' : 'Agregar a favoritos'}>
                            <IconButton size="small" onClick={() => toggleFavorito(e)} sx={{ p: 0.2 }}>
                              {esFav
                                ? <StarIcon sx={{ fontSize: 15, color: '#f59e0b' }} />
                                : <StarBorderIcon sx={{ fontSize: 15, color: '#cbd5e1', '&:hover': { color: '#f59e0b' } }} />
                              }
                            </IconButton>
                          </Tooltip>
                          <IconButton size="small" onClick={() => quitarEstudio(e.id)} sx={{ p: 0.2, color: '#cbd5e1', '&:hover': { color: '#ef4444' } }}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* Indicaciones */}
            <TextField
              fullWidth size="small" multiline minRows={2}
              label="Indicaciones clínicas"
              placeholder="Ayuno 8-12 hrs, muestra matutina, etc."
              value={indicaciones}
              onChange={(e) => setIndicaciones(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2, background: '#fff', fontSize: 12 } }}
            />

            {/* Botón */}
            <Button
              variant="contained" fullWidth
              startIcon={<AddIcon />}
              disabled={estudiosSeleccionados.length === 0 || !consultaId}
              onClick={guardarOrden}
              sx={{
                borderRadius: 2, fontWeight: 700, fontSize: 13, textTransform: 'none', py: 1.2,
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)', boxShadow: '0 6px 16px rgba(245, 158, 11, 0.4)' },
                '&:disabled': { background: '#e2e8f0', boxShadow: 'none' },
              }}
            >
              Agregar orden de laboratorio
            </Button>
          </Box>
        </Box>
      )}

      {tipoSeleccionado === 'interconsulta' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {/* Columna izquierda: datos de la interconsulta */}
          <Box sx={{ p: 2, borderRadius: 3, background: '#f0f9ff', border: '1px solid #bae6fd' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 2 }}>
              <Box sx={{
                width: 30, height: 30, borderRadius: 2,
                background: '#0ea5e9', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <HospitalIcon sx={{ fontSize: 18, color: '#fff' }} />
              </Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0c4a6e' }}>
                Referencia al especialista
              </Typography>
            </Box>

            <TextField
              fullWidth size="small" label="Especialidad"
              placeholder="Ej: Cardiología, Endocrinología, Traumatología..."
              value={especialidad} onChange={(e) => setEspecialidad(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2, background: '#fff', fontSize: 13, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0ea5e9', borderWidth: 2 } } }}
            />
            <TextField
              fullWidth size="small" label="Motivo y preguntas al especialista" multiline minRows={5}
              placeholder="Describa el motivo de la referencia, hallazgos clínicos relevantes, estudios realizados, tratamiento actual y preguntas específicas para el especialista..."
              value={motivo} onChange={(e) => setMotivo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, background: '#fff', fontSize: 13, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0ea5e9', borderWidth: 2 } } }}
            />
          </Box>

          {/* Columna derecha: datos del especialista + botón */}
          <Box sx={{
            p: 2, borderRadius: 3,
            background: especialidad ? '#f0f9ff' : '#f8fafc',
            border: `2px solid ${especialidad ? '#bae6fd' : '#e2e8f0'}`,
            display: 'flex', flexDirection: 'column',
            transition: 'all 0.3s',
          }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#0c4a6e', letterSpacing: 0.5, textTransform: 'uppercase', mb: 1.5 }}>
              Datos del especialista (opcional)
            </Typography>

            <TextField
              fullWidth size="small" label="Nombre del especialista"
              placeholder="Dr./Dra. nombre completo"
              value={nombreEspecialista} onChange={(e) => setNombreEspecialista(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2, background: '#fff', fontSize: 13, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0ea5e9', borderWidth: 2 } } }}
            />
            <TextField
              fullWidth size="small" label="Teléfono del especialista"
              placeholder="Número de contacto del especialista"
              value={telefonoEspecialista} onChange={(e) => setTelefonoEspecialista(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, background: '#fff', fontSize: 13, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0ea5e9', borderWidth: 2 } } }}
            />

            {/* Resumen visual */}
            {especialidad && (
              <Box sx={{ p: 1.5, borderRadius: 2, background: '#e0f2fe', border: '1px solid #7dd3fc', mb: 2 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#0c4a6e' }}>
                  Interconsulta a: <strong>{especialidad}</strong>
                </Typography>
                {nombreEspecialista && (
                  <Typography sx={{ fontSize: 11, color: '#0369a1', mt: 0.3 }}>
                    Especialista: {nombreEspecialista}
                  </Typography>
                )}
                {motivo && (
                  <Typography sx={{ fontSize: 10, color: '#0369a1', mt: 0.3, fontStyle: 'italic' }} noWrap>
                    {motivo.substring(0, 80)}{motivo.length > 80 ? '...' : ''}
                  </Typography>
                )}
              </Box>
            )}

            <Box sx={{ flex: 1 }} />

            <Button
              variant="contained" fullWidth
              startIcon={<AddIcon />}
              disabled={!especialidad || !motivo || !consultaId}
              onClick={guardarOrden}
              sx={{
                borderRadius: 2, fontWeight: 700, fontSize: 13, textTransform: 'none', py: 1.2,
                background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                boxShadow: '0 4px 12px rgba(14, 165, 233, 0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)', boxShadow: '0 6px 16px rgba(14, 165, 233, 0.4)' },
                '&:disabled': { background: '#e2e8f0', boxShadow: 'none' },
              }}
            >
              Agregar interconsulta
            </Button>
          </Box>
        </Box>
      )}

      {/* Orden de hospitalización */}
      {tipoSeleccionado === 'hospitalizacion' && (
        <Box sx={{ p: 2.5, borderRadius: 3, background: '#fef2f2', border: '2px solid #fecaca' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 2 }}>
            <Box sx={{
              width: 30, height: 30, borderRadius: 2,
              background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <HotelIcon sx={{ fontSize: 18, color: '#fff' }} />
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#991b1b' }}>
              Orden de hospitalización
            </Typography>
          </Box>

          {/* Nombre del paciente — auto-llenado */}
          <TextField
            fullWidth size="small"
            label="Paciente"
            value={`Favor de hospitalizar a: ${paciente?.nombre_completo || '—'}`}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2, background: '#fff', fontSize: 13, fontWeight: 600, color: '#991b1b' } }}
          />

          {/* Indicaciones hospitalarias */}
          <TextField
            fullWidth size="small" multiline minRows={4}
            label="Indicaciones hospitalarias"
            placeholder="Diagnóstico de ingreso, servicio, dieta, soluciones IV, medicamentos, estudios a solicitar, cuidados de enfermería, signos vitales cada X horas..."
            value={indicacionesHosp}
            onChange={(e) => setIndicacionesHosp(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2, background: '#fff', fontSize: 13, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ef4444', borderWidth: 2 } } }}
          />

          {/* Indicaciones para el paciente */}
          <TextField
            fullWidth size="small" multiline minRows={3}
            label="Indicaciones para el paciente"
            placeholder="Qué traer al hospital, ayuno, documentos necesarios, acompañante, horario de ingreso..."
            value={indicacionesPaciente}
            onChange={(e) => setIndicacionesPaciente(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, background: '#fff', fontSize: 13, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ef4444', borderWidth: 2 } } }}
          />

          <Button
            variant="contained" fullWidth
            startIcon={<AddIcon />}
            disabled={!indicacionesHosp || !consultaId}
            onClick={guardarOrden}
            sx={{
              borderRadius: 2, fontWeight: 700, fontSize: 13, textTransform: 'none', py: 1.2,
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' },
              '&:disabled': { background: '#e2e8f0', boxShadow: 'none' },
            }}
          >
            Agregar orden de hospitalización
          </Button>
        </Box>
      )}

      {/* Certificado médico */}
      {tipoSeleccionado === 'certificado' && (
        <Box sx={{ p: 2.5, borderRadius: 3, background: '#f0fdf4', border: '2px solid #bbf7d0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 2 }}>
            <Box sx={{ width: 30, height: 30, borderRadius: 2, background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CertIcon sx={{ fontSize: 18, color: '#fff' }} />
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#065f46' }}>
              Certificado médico
            </Typography>
          </Box>

          {/* Texto auto-generado */}
          <Box sx={{ p: 1.5, borderRadius: 2, background: '#ecfdf5', border: '1px solid #a7f3d0', mb: 1.5 }}>
            <Typography sx={{ fontSize: 8.5, color: '#065f46', lineHeight: 1.6 }}>
              <strong>A quien corresponda</strong>
            </Typography>
            <Typography sx={{ fontSize: 8.5, color: '#065f46', lineHeight: 1.6, mt: 0.5 }}>
              Después de haber practicado un examen médico minucioso, a <strong>{(paciente?.nombre_completo || '—').toUpperCase()}</strong> de sexo <strong>{paciente?.sexo === 'femenino' ? 'Mujer' : paciente?.sexo === 'masculino' ? 'Hombre' : '—'}</strong> con <strong>{paciente?.fecha_nacimiento ? Math.floor((Date.now() - new Date(paciente.fecha_nacimiento).getTime()) / (365.25 * 24 * 3600 * 1000)) : '—'} años</strong> de edad, certifico que tiene el siguiente diagnóstico:
            </Typography>
          </Box>

          <TextField
            fullWidth size="small" multiline minRows={4}
            label="Diagnóstico"
            placeholder="Escriba el diagnóstico que aparecerá en el certificado..."
            value={diagnosticoCert}
            onChange={(e) => setDiagnosticoCert(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2, background: '#fff', fontSize: 13, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#10b981', borderWidth: 2 } } }}
          />

          <TextField
            fullWidth size="small" multiline minRows={2}
            label="Observaciones adicionales (opcional)"
            placeholder="Recomendaciones, restricciones, tiempo de reposo..."
            value={observacionesCert}
            onChange={(e) => setObservacionesCert(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, background: '#fff', fontSize: 13, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#10b981', borderWidth: 2 } } }}
          />

          <Button
            variant="contained" fullWidth
            startIcon={<AddIcon />}
            disabled={!diagnosticoCert || !consultaId}
            onClick={guardarOrden}
            sx={{
              borderRadius: 2, fontWeight: 700, fontSize: 13, textTransform: 'none', py: 1.2,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' },
              '&:disabled': { background: '#e2e8f0', boxShadow: 'none' },
            }}
          >
            Agregar certificado médico
          </Button>
        </Box>
      )}

      {/* Justificante médico */}
      {tipoSeleccionado === 'justificante' && (
        <Box sx={{ p: 2.5, borderRadius: 3, background: '#f5f3ff', border: '2px solid #ddd6fe' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 2 }}>
            <Box sx={{ width: 30, height: 30, borderRadius: 2, background: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <JustIcon sx={{ fontSize: 18, color: '#fff' }} />
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#5b21b6' }}>
              Justificante médico
            </Typography>
          </Box>

          <Box sx={{ p: 1.5, borderRadius: 2, background: '#ede9fe', border: '1px solid #c4b5fd', mb: 1.5 }}>
            <Typography sx={{ fontSize: 8.5, color: '#5b21b6', lineHeight: 1.6 }}>
              <strong>A quien corresponda</strong>
            </Typography>
            <Typography sx={{ fontSize: 8.5, color: '#5b21b6', lineHeight: 1.6, mt: 0.5 }}>
              Por medio de la presente se hace constar que el paciente <strong>{(paciente?.nombre_completo || '—').toUpperCase()}</strong> de sexo <strong>{paciente?.sexo === 'femenino' ? 'Mujer' : paciente?.sexo === 'masculino' ? 'Hombre' : '—'}</strong> con <strong>{paciente?.fecha_nacimiento ? Math.floor((Date.now() - new Date(paciente.fecha_nacimiento).getTime()) / (365.25 * 24 * 3600 * 1000)) : '—'} años</strong> edad, presenta el siguiente diagnóstico:
            </Typography>
          </Box>

          <TextField
            fullWidth size="small" multiline minRows={4}
            label="Diagnóstico"
            placeholder="Escriba el diagnóstico que aparecerá en el justificante..."
            value={diagnosticoJust}
            onChange={(e) => setDiagnosticoJust(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2, background: '#fff', fontSize: 13, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#8b5cf6', borderWidth: 2 } } }}
          />

          <TextField
            fullWidth size="small" multiline minRows={2}
            label="Observaciones adicionales (opcional)"
            placeholder="Días de incapacidad, restricciones, fecha de próxima cita..."
            value={observacionesJust}
            onChange={(e) => setObservacionesJust(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, background: '#fff', fontSize: 13, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#8b5cf6', borderWidth: 2 } } }}
          />

          <Button
            variant="contained" fullWidth
            startIcon={<AddIcon />}
            disabled={!diagnosticoJust || !consultaId}
            onClick={guardarOrden}
            sx={{
              borderRadius: 2, fontWeight: 700, fontSize: 13, textTransform: 'none', py: 1.2,
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' },
              '&:disabled': { background: '#e2e8f0', boxShadow: 'none' },
            }}
          >
            Agregar justificante médico
          </Button>
        </Box>
      )}

      {/* Orden libre */}
      {tipoSeleccionado === 'libre' && (
        <Box sx={{ p: 2, borderRadius: 3, background: tipoInfo ? `${tipoInfo.color}08` : '#f8fafc', border: `2px solid ${tipoInfo ? tipoInfo.color : '#e2e8f0'}20` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 2 }}>
            <Box sx={{
              width: 30, height: 30, borderRadius: 2,
              background: tipoInfo?.color || '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {tipoInfo && React.cloneElement(tipoInfo.icon, { sx: { fontSize: 18, color: '#fff' } })}
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
              {tipoInfo?.label || 'Orden'}
            </Typography>
          </Box>
          <TextField
            fullWidth size="small" multiline minRows={6}
            placeholder={{
              certificado: 'Certifico que el/la paciente se encuentra apto/no apto para...',
              justificante: 'El/la paciente acudió a consulta el día... Se otorgan X días de incapacidad...',
              libre: 'Contenido de la orden...',
            }[tipoSeleccionado] || ''}
            value={textoLibre} onChange={(e) => setTextoLibre(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2, background: '#fff', fontSize: 13 } }}
          />
          <Button variant="contained" fullWidth startIcon={<AddIcon />}
            disabled={!textoLibre || !consultaId} onClick={guardarOrden}
            sx={{
              borderRadius: 2, fontWeight: 700, fontSize: 13, textTransform: 'none', py: 1.2,
              background: `linear-gradient(135deg, ${tipoInfo?.color || '#64748b'} 0%, ${tipoInfo?.color || '#64748b'}dd 100%)`,
              boxShadow: `0 4px 12px ${tipoInfo?.color || '#64748b'}40`,
              '&:hover': { opacity: 0.9 },
              '&:disabled': { background: '#e2e8f0', boxShadow: 'none' },
            }}
          >
            Agregar {tipoInfo?.label?.toLowerCase() || 'orden'}
          </Button>
        </Box>
      )}

        </Box>
      </Box>

      {/* Órdenes ya creadas */}
      {ordenes.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: 0.5, textTransform: 'uppercase', mb: 1 }}>
            Documentos generados ({ordenes.length})
          </Typography>
          {ordenes.map(o => {
            const tipo = TIPOS_ORDEN.find(t => t.value === o.tipo);
            return (
              <Box key={o.id} sx={{
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                p: 1.5, mb: 1, borderRadius: 2,
                background: '#f8fafc', border: '1px solid #e2e8f0',
              }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5 }}>
                    <Chip
                      label={tipo?.label || o.tipo}
                      size="small"
                      sx={{ height: 22, fontWeight: 600, fontSize: 10, bgcolor: tipo?.color || '#64748b', color: '#fff', '& .MuiChip-label': { px: 1 } }}
                    />
                  </Box>
                  {o.tipo === 'laboratorio' && o.contenido?.estudios && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.5 }}>
                      {o.contenido.estudios.map((e, i) => (
                        <Chip key={i} label={e.nombre} size="small" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                      ))}
                    </Box>
                  )}
                  {o.tipo === 'interconsulta' && (
                    <Box sx={{ mt: 0.3 }}>
                      <Typography sx={{ fontSize: 12, color: '#475569' }}>
                        <strong>{o.contenido?.especialidad}</strong> — {o.contenido?.motivo?.substring(0, 100)}{o.contenido?.motivo?.length > 100 ? '...' : ''}
                      </Typography>
                      {(o.contenido?.nombre_especialista || o.contenido?.telefono_especialista) && (
                        <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.2 }}>
                          {o.contenido?.nombre_especialista}{o.contenido?.telefono_especialista ? ` · ${o.contenido.telefono_especialista}` : ''}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {o.tipo === 'hospitalizacion' && (
                    <Box sx={{ mt: 0.3 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#991b1b' }}>
                        {o.contenido?.paciente}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#475569', mt: 0.2 }} noWrap>
                        {o.contenido?.indicaciones_hospitalarias?.substring(0, 100)}{(o.contenido?.indicaciones_hospitalarias?.length || 0) > 100 ? '...' : ''}
                      </Typography>
                    </Box>
                  )}
                  {o.tipo === 'certificado' && (
                    <Box sx={{ mt: 0.3 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#065f46' }}>
                        {o.contenido?.paciente}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#475569', mt: 0.2 }} noWrap>
                        Dx: {o.contenido?.diagnostico?.substring(0, 100)}{(o.contenido?.diagnostico?.length || 0) > 100 ? '...' : ''}
                      </Typography>
                    </Box>
                  )}
                  {o.tipo === 'justificante' && (
                    <Box sx={{ mt: 0.3 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#5b21b6' }}>
                        {o.contenido?.paciente}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#475569', mt: 0.2 }} noWrap>
                        Dx: {o.contenido?.diagnostico?.substring(0, 100)}{(o.contenido?.diagnostico?.length || 0) > 100 ? '...' : ''}
                      </Typography>
                    </Box>
                  )}
                  {o.tipo === 'libre' && (
                    <Typography sx={{ fontSize: 12, color: '#475569', mt: 0.3 }}>
                      {o.contenido?.texto?.substring(0, 120)}{o.contenido?.texto?.length > 120 ? '...' : ''}
                    </Typography>
                  )}
                  {o.indicaciones && (
                    <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.3, fontStyle: 'italic' }}>
                      {o.indicaciones}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, flexShrink: 0 }}>
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => editarOrden(o)} sx={{ color: '#f59e0b', '&:hover': { color: '#d97706' } }}>
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  {o.tipo === 'laboratorio' && (
                    <>
                      <Tooltip title="Descargar PDF">
                        <IconButton size="small" onClick={() => exportOrdenLaboratorioPDF({ paciente, orden: o, imprimir: false })} sx={{ color: '#f59e0b', '&:hover': { color: '#d97706' } }}>
                          <PdfIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Imprimir">
                        <IconButton size="small" onClick={() => exportOrdenLaboratorioPDF({ paciente, orden: o, imprimir: true })} sx={{ color: '#0ea5e9', '&:hover': { color: '#0284c7' } }}>
                          <PrintIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {o.tipo === 'interconsulta' && (
                    <>
                      <Tooltip title="Descargar PDF">
                        <IconButton size="small" onClick={() => exportInterconsultaPDF({ paciente, orden: o, imprimir: false })} sx={{ color: '#0ea5e9', '&:hover': { color: '#0284c7' } }}>
                          <PdfIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Imprimir">
                        <IconButton size="small" onClick={() => exportInterconsultaPDF({ paciente, orden: o, imprimir: true })} sx={{ color: '#0ea5e9', '&:hover': { color: '#0284c7' } }}>
                          <PrintIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {o.tipo === 'hospitalizacion' && (
                    <>
                      <Tooltip title="Descargar PDF">
                        <IconButton size="small" onClick={() => exportOrdenHospitalizacionPDF({ paciente, orden: o, imprimir: false })} sx={{ color: '#ef4444', '&:hover': { color: '#dc2626' } }}>
                          <PdfIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Imprimir">
                        <IconButton size="small" onClick={() => exportOrdenHospitalizacionPDF({ paciente, orden: o, imprimir: true })} sx={{ color: '#0ea5e9', '&:hover': { color: '#0284c7' } }}>
                          <PrintIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {o.tipo === 'certificado' && (
                    <>
                      <Tooltip title="Descargar PDF">
                        <IconButton size="small" onClick={() => exportCertificadoPDF({ paciente, orden: o, imprimir: false })} sx={{ color: '#10b981', '&:hover': { color: '#059669' } }}>
                          <PdfIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Imprimir">
                        <IconButton size="small" onClick={() => exportCertificadoPDF({ paciente, orden: o, imprimir: true })} sx={{ color: '#0ea5e9', '&:hover': { color: '#0284c7' } }}>
                          <PrintIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {o.tipo === 'justificante' && (
                    <>
                      <Tooltip title="Descargar PDF">
                        <IconButton size="small" onClick={() => exportJustificantePDF({ paciente, orden: o, imprimir: false })} sx={{ color: '#8b5cf6', '&:hover': { color: '#7c3aed' } }}>
                          <PdfIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Imprimir">
                        <IconButton size="small" onClick={() => exportJustificantePDF({ paciente, orden: o, imprimir: true })} sx={{ color: '#0ea5e9', '&:hover': { color: '#0284c7' } }}>
                          <PrintIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  <IconButton size="small" onClick={() => eliminarOrden(o.id)} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
